import { Button, Card, Conditional } from "@result/rui/components";
import { useNotifications } from "@result/rui/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ActionButton from "#/components/ActionButton.jsx";
import PageHeader from "#/components/PageHeader.jsx";
import TemplateDownloadButton from "#/components/import/TemplateDownloadButton";
import useAuthorization from "#/hooks/useAuthorization";
import ApiClient from "#/redux/api/ApiClient";
import { IsAuthorized } from "#/components/Authorization";
import { DROBNICA_STAFF_ROLES } from "#/def/drobnicaRoles";

export const BreedingValuesChartOptions = {
  groups: {
    A: {
      key: "A",
      color: "#2473af",
    },
    B: {
      key: "B",
      color: "#63af7b",
    },
  },
  titleColor: "#666666",
  minHeight: 500,
  maxWidth: 800,
  options: {
    dataZoom: [
      {
        type: "inside", // mouse scroll + drag zoom
        throttle: 50,
      },
      {
        type: "slider", // visible zoom bar
        show: true,
        height: 30,
        bottom: 30,
        handleStyle: {
          color: "#17A1FAE6", // Thumb (drag handle)
          borderColor: "#17A1FA", // Optional border
        },
        backgroundColor: "#EEEEEF", // Track background (optional: use $light)
        fillerColor: "#17A1FA33", // Selected area in track (30% opacity of $primary)
        borderColor: "#17A1FA", // Border of the whole slider
      },
    ],
  },
};

const BREEDING_VALUES_UPLOAD_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

const BreedingValuesPage = () => {
  const { t } = useTranslation();
  const { addSuccessNotification, addErrorNotification } = useNotifications();
  const [isUploading, setIsUploading] = useState(false);

  const authorization = useAuthorization();

  const uploadCSV = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append(
          "file",
          JSON.stringify({
            filename: "genotype.csv",
          })
        );
        const endpoint =
          "/api/plemenske_vrednosti/" +
          (authorization.isAdmin ? "upload_with_sifranti" : "upload");
        ApiClient.postForFile(endpoint, formData, undefined, {
          timeout: BREEDING_VALUES_UPLOAD_TIMEOUT_MS,
        })
          .then(() => {
            setIsUploading(false);
            addSuccessNotification(t("breedingValues.uploadCSV.success"));
          })
          .catch(async (error) => {
            setIsUploading(false);
            let responseError = null;
            try {
              const blobText = await error.response?.data.text();
              const jsonData = JSON.parse(blobText);
              responseError = jsonData.message;
              responseError += jsonData.errors.map((e) => e).join("\n");
              console.log(responseError);
            } catch (error) {
              console.error("Error parsing JSON:", error);
            }

            addErrorNotification(
              responseError ?? t("breedingValues.uploadCSV.error")
            );
          });
      }
    };
    input.click();
  };

  return (
    <div>
      <PageHeader
        title={t("breedingValues")}
        subtitle={t("breedingValues.description")}
        backURL={authorization.isKIS ? undefined : ".."}
        actions={[
          <IsAuthorized as={DROBNICA_STAFF_ROLES} >
            <Button
              label={t("breedingValues.uploadCSV")}
              color="primary"
              onClick={uploadCSV}
              isLoading={isUploading}
            />
          </IsAuthorized>,
          <TemplateDownloadButton
            downloadPath={"/api/plemenske_vrednosti/download_csv"}
          />,
        ]}
      />
      <Card title={t("breedingValues.selectOption")}>
        <div className="fixed-grid has-1-cols has-3-cols-tablet has-5-cols-desktop">
          <div className="grid">
            <ActionButton
              title={t("breedingValues.catalog")}
              description={t("breedingValues.catalog.description")}
              to="catalog"
            />
            <ActionButton
              title={t("breedingValues.rank")}
              description={t("breedingValues.rank.description")}
              to="rank"
            />
            <ActionButton
              title={t("breedingValues.comparisonRank")}
              description={t("breedingValues.comparisonRank.description")}
              to="comparison-rang"
            />
            <Conditional
              condition={
                authorization.isBF ||
                authorization.isSelector ||
                authorization.isVF ||
                authorization.isAdmin ||
                authorization.isKIS
              }>
              <ActionButton
                title={t("breedingValues.comparisonGeneticTrend")}
                description={t(
                  "breedingValues.comparisonGeneticTrend.description"
                )}
                to="genetic-trend"
              />
            </Conditional>
            <Conditional
              condition={
                authorization.isBF ||
                authorization.isAdmin ||
                authorization.isKIS
              }>
              <ActionButton
                title={t("breedingValues.dataDownload")}
                description={t("breedingValues.dataDownload.description")}
                to="download"
              />
            </Conditional>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BreedingValuesPage;
